import React, { useEffect, useState } from 'react';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

import { ConnectorNthPageCard } from '../components/ConnectorNthPageCard.tsx';
import { ConnectorPostConnectPageCard } from '../components/ConnectorPostConnectPageCard.tsx';
import { useConnectorPromoConfig } from '../hooks/useConnectorPromoConfig.ts';

function getSearchPagePromoVariant(
	shouldShowPostConnectPagePromo: boolean,
	shouldShowNthPagePromo: boolean,
): string | null {
	switch (true) {
		case shouldShowPostConnectPagePromo:
			return 'postConnectPagePromo';
		case shouldShowNthPagePromo:
			return 'nthPagePromo';
		default:
			return null;
	}
}

/**
 * Self-contained surface component for the full search results page.
 * Internally calls useConnectorPromoConfig and renders the page-variant promo cards.
 */
export function ConnectorPromoSearchPage(): React.JSX.Element | null {
	// Full search page always has "recent items" context (user has searched)
	const config = useConnectorPromoConfig({ hasRecentItems: true });
	const { createEvent } = useConfluenceAnalyticsEvents();
	// Session-only: hides the post-connect card after "Maybe Later" without persisting the dismiss.
	const [maybeLaterDismissed, setMaybeLaterDismissed] = useState(false);

	const promoVariant = getSearchPagePromoVariant(
		config.shouldShowPostConnectPagePromo,
		config.shouldShowNthPagePromo,
	);

	useEffect(() => {
		if (!promoVariant) {
			return;
		}
		createEvent({
			type: 'sendTrackEvent',
			data: {
				source: 'ConnectorPromoSearchPage',
				action: 'viewed',
				actionSubject: 'connectorPromo',
				attributes: {
					promoVariant,
					container: 'ConnectorPromoSearchPage',
				},
			},
		}).fire();
	}, [promoVariant, createEvent]);

	if (config.shouldShowPostConnectPagePromo && !maybeLaterDismissed) {
		return (
			<ConnectorPostConnectPageCard
				connectedAppName={config.connectedAppName}
				nextRecommendedConnector={config.nextRecommendedConnector}
				onSetupConnector={config.onSetupConnector}
				onMaybeLater={() => setMaybeLaterDismissed(true)}
			/>
		);
	}

	if (config.shouldShowNthPagePromo) {
		return (
			<ConnectorNthPageCard
				recommendedConnectors={config.recommendedConnectors}
				onSetupConnector={config.onSetupConnector}
				onDismissPromo={() => config.onDismissPromo('banner')}
			/>
		);
	}

	return null;
}
